import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import DOMPurify from 'dompurify'

import { F0Button, F0Dialog, F0Text } from '@factorialco/f0-react'
import { Stack, WidgetInboxList } from '@factorialco/f0-react/dist/experimental'
import { CommunityPostListDocument } from 'generated/resources/graphql'
import type { CommunityPostDataFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import i18n from 'modules/core/lib/i18n'
import { getQueryFiltersFromDropdown } from 'modules/posts/components/CommunityPostList/helpers'

import { CommunitiesDropdown } from 'modules/dashboard/components/Groups/types'

const DESCRIPTION_MAX_LENGTH = 80

type Props = {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  selectedCommunity: CommunitiesDropdown
}

function authorDisplayName(author: CommunityPostDataFragment['author']): string {
  if (!author) return ''
  const preferred = author.employee?.preferredName
  if (preferred) return preferred
  return [author.firstName, author.lastName].filter(Boolean).join(' ').trim() || ''
}

/** Returns plain text safe for display (no HTML). */
function truncate(text: string | null | undefined, max: number): string {
  if (!text) return ''
  const plain = DOMPurify.sanitize(text, { ALLOWED_TAGS: [] })
  if (plain.length <= max) return plain
  return `${plain.slice(0, max).trim()}…`
}

export const PinnedPostsModal: React.FC<Props> = ({ isOpen, onOpenChange, selectedCommunity }) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const {
    data,
    loading,
    controls: { fetchMore, hasMore, loading: loadingMore },
  } = usePaginatedQuery(CommunityPostListDocument, {
    variables: getQueryFiltersFromDropdown(selectedCommunity, { pinned: true }),
    extractConnection: (d) =>
      d.posts.postsConnection ?? {
        pageInfo: { hasNextPage: false, endCursor: null },
        edges: [],
      },
    skip: !isOpen,
  })

  const edges = data?.posts.postsConnection?.edges ?? []
  const posts = edges.map((e) => e.node)

  const handlePostClick = (postId: string) => {
    onOpenChange(false)
    navigate(navigation.communities.post.id.fullPath({ id: postId }))
  }

  const items = React.useMemo(
    () =>
      posts.map((post) => {
        const name = authorDisplayName(post.author)
        const description = truncate(post.description ?? post.title ?? null, DESCRIPTION_MAX_LENGTH)
        return {
          id: String(post.id),
          title: name || i18n.t('posts.communities.user'),
          subtitle: description || '—',
        }
      }),
    [posts]
  )

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={() => onOpenChange(false)}
      title={i18n.t('posts.communities.pinned_messages')}
      width="md"
    >
      {isOpen && (
        <>
          {loading && (
            <Stack paddingX="p-4" paddingY="p-4">
              <F0Text variant="description" content={i18n.t('common.loading')} />
            </Stack>
          )}
          {!loading && posts.length === 0 && (
            <Stack paddingX="p-4" paddingY="p-4">
              <F0Text
                variant="description"
                content={i18n.t('posts.communities.pinned_empty_title')}
              />
            </Stack>
          )}
          {!loading && items.length > 0 && (
            <WidgetInboxList
              items={items}
              onClickItem={(id) => handlePostClick(String(id))}
              showAllItems
            />
          )}
          {!loading && hasMore && (
            <Stack paddingX="p-4" paddingY="p-4" justifyContent="center">
              <F0Button variant="ghost" disabled={loadingMore} onClick={() => fetchMore()}>
                {loadingMore ? i18n.t('common.loading') : i18n.t('common.load_more')}
              </F0Button>
            </Stack>
          )}
        </>
      )}
    </F0Dialog>
  )
}
